import { MAX_ENTITY_NAME } from "@/constants/entityLimits";
import {
  BookMarked,
  FileSearch,
  GitMerge,
  Library,
  type LucideIcon,
  MessageSquareText,
  ShieldCheck,
  ShieldQuestion,
  Sparkles,
  SpellCheck,
  Braces,
  Wrench,
  PackageSearch,
  ScanSearch,
} from "lucide-react";
import type { useTranslations } from "next-intl";
import type { Messages } from "@/i18n/messages";
import { isRecord } from "@/utils/isRecord";

export type Expected = Record<string, unknown>;

export type CheckDraft = {
  evaluatorId?: string;
  type: string;
  expected: Expected;
  name?: string;
};

export type CheckGroup = "Response" | "Knowledge" | "Tools" | "Combine";

export type AgentTestsT = ReturnType<typeof useTranslations<"agentTests">>;

type ChecksMessages = Messages["agentTests"]["checks"];

export type CheckKindKey = keyof ChecksMessages["kinds"];

export type CheckModeType = keyof ChecksMessages["modes"];

export type CheckMode = { type: CheckModeType };

export type CheckKind = {
  key: CheckKindKey;
  group: CheckGroup;
  icon: LucideIcon;
  modes: CheckMode[];
};

export const CHECK_GROUPS: CheckGroup[] = [
  "Response",
  "Knowledge",
  "Tools",
  "Combine",
];

export const CHECK_KINDS: CheckKind[] = [
  {
    key: "meets_expectation",
    group: "Response",
    icon: Sparkles,
    modes: [{ type: "agent_conversation_judge" }],
  },
  {
    key: "says",
    group: "Response",
    icon: MessageSquareText,
    modes: [
      { type: "agent_response_contains" },
      { type: "agent_response_not_contains" },
      { type: "agent_response_regex" },
      { type: "agent_text_equals" },
    ],
  },
  {
    key: "similar",
    group: "Response",
    icon: ScanSearch,
    modes: [{ type: "agent_llm_judge" }],
  },
  {
    key: "correct",
    group: "Response",
    icon: SpellCheck,
    modes: [{ type: "answer_correctness" }],
  },
  {
    key: "grounded",
    group: "Knowledge",
    icon: ShieldCheck,
    modes: [{ type: "answer_faithfulness" }],
  },
  {
    key: "sufficient",
    group: "Knowledge",
    icon: Library,
    modes: [{ type: "answer_sufficiency" }],
  },
  {
    key: "refusal",
    group: "Knowledge",
    icon: ShieldQuestion,
    modes: [{ type: "answer_refusal" }],
  },
  {
    key: "citations",
    group: "Knowledge",
    icon: BookMarked,
    modes: [
      { type: "agent_has_ref_tags" },
      { type: "agent_ref_matches_kb_document" },
      { type: "agent_no_plain_citations" },
    ],
  },
  {
    key: "retrieved_documents",
    group: "Knowledge",
    icon: FileSearch,
    modes: [{ type: "agent_retrieved_documents" }],
  },
  {
    key: "searched_knowledge_base",
    group: "Knowledge",
    icon: Library,
    modes: [{ type: "agent_searched_knowledge_base" }],
  },
  {
    key: "tool_called",
    group: "Tools",
    icon: Wrench,
    modes: [{ type: "agent_tool_called" }, { type: "agent_tool_not_called" }],
  },
  {
    key: "tool_arguments",
    group: "Tools",
    icon: Braces,
    modes: [
      { type: "agent_tool_arguments" },
      { type: "agent_tool_arg_keys" },
      { type: "agent_tool_argument_judge" },
    ],
  },
  {
    key: "tool_result",
    group: "Tools",
    icon: PackageSearch,
    modes: [
      { type: "agent_tool_return_judge" },
      { type: "agent_tool_return_contains" },
    ],
  },
  {
    key: "combine",
    group: "Combine",
    icon: GitMerge,
    modes: [{ type: "agent_any_of" }, { type: "agent_all_of" }],
  },
];

export const COMPOSITE_TYPES = ["agent_any_of", "agent_all_of"];

export const COMPOSITE_CHILD_TYPES = [
  "agent_conversation_judge",
  "agent_response_contains",
  "agent_response_not_contains",
  "agent_response_regex",
  "agent_has_ref_tags",
  "agent_ref_matches_kb_document",
  "agent_no_plain_citations",
  "agent_tool_called",
  "agent_tool_not_called",
  "agent_tool_arguments",
  "agent_tool_arg_keys",
  "agent_tool_argument_judge",
  "agent_tool_return_judge",
  "agent_tool_return_contains",
];

const NO_VALUE_TYPES = [
  "answer_faithfulness",
  "answer_sufficiency",
  "agent_no_plain_citations",
];

export function needsNoValue(type: string): boolean {
  return NO_VALUE_TYPES.includes(type);
}

export function kindOf(type: string): CheckKind | undefined {
  return CHECK_KINDS.find((kind) => kind.modes.some((m) => m.type === type));
}

export function checkName(t: AgentTestsT, kind: CheckKind): string {
  return t(`checks.kinds.${kind.key}.name`);
}

export function checkDescription(t: AgentTestsT, kind: CheckKind): string {
  return t(`checks.kinds.${kind.key}.description`);
}

export function modeLabel(t: AgentTestsT, type: string): string {
  const mode = kindOf(type)?.modes.find((m) => m.type === type);
  return mode ? t(`checks.modes.${mode.type}`) : type;
}

export function groupLabel(t: AgentTestsT, group: CheckGroup): string {
  return t(`checks.groups.${group}`);
}

export function checkLabel(t: AgentTestsT, type: string): string {
  const kind = kindOf(type);
  if (!kind) {
    return type;
  }
  const mode = kind.modes.find((m) => m.type === type);
  return kind.modes.length > 1 && mode
    ? t("checks.label", {
        kind: checkName(t, kind),
        mode: t(`checks.modes.${mode.type}`),
      })
    : checkName(t, kind);
}

export function defaultExpected(type: string): Expected {
  switch (type) {
    case "agent_conversation_judge":
      return { rubric: "" };
    case "agent_response_contains":
    case "agent_response_not_contains":
      return { text: "", case_sensitive: false };
    case "agent_response_regex":
      return { pattern: "" };
    case "agent_text_equals":
      return { text: "", message_index: -1 };
    case "agent_llm_judge":
      return { reference_text: "", message_index: -1 };
    case "answer_correctness":
      return { gold_answer: "" };
    case "answer_refusal":
      return { expect_refusal: true };
    case "agent_has_ref_tags":
      return { min_count: 1 };
    case "agent_ref_matches_kb_document":
      return { known_filenames: [], known_doc_ids: [] };
    case "agent_retrieved_documents":
      return { documents: [] };
    case "agent_searched_knowledge_base":
      return { kb_ids: [], no_search_ok: false };
    case "agent_tool_called":
    case "agent_tool_not_called":
      return { tool_name: "" };
    case "agent_tool_arguments":
      return { tool_name: "", expected_args: {}, partial_match: true };
    case "agent_tool_arg_keys":
      return { tool_name: "", present: [], absent: [] };
    case "agent_tool_argument_judge":
      return { tool_name: "", argument_name: "", rubric: "" };
    case "agent_tool_return_judge":
      return { tool_name: "", rubric: "" };
    case "agent_tool_return_contains":
      return { tool_name: "", text: "" };
    case "agent_any_of":
    case "agent_all_of":
      return { children: [] };
    default:
      return {};
  }
}

export function switchMode(type: string, expected: Expected): Expected {
  const next = defaultExpected(type);
  for (const key of ["tool_name", "text"]) {
    if (key in next && typeof expected[key] === "string") {
      next[key] = expected[key];
    }
  }
  if ("pattern" in next && typeof expected.text === "string") {
    next.pattern = expected.text;
  }
  if ("text" in next && typeof expected.pattern === "string") {
    next.text = expected.pattern;
  }
  if (COMPOSITE_TYPES.includes(type) && Array.isArray(expected.children)) {
    next.children = expected.children;
  }
  return next;
}

function text(expected: Expected, key: string): string {
  const value = expected[key];
  return typeof value === "string" ? value : "";
}

function strings(expected: Expected, key: string): string[] {
  const value = expected[key];
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string")
    : [];
}

function records(expected: Expected, key: string): Expected[] {
  const value = expected[key];
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

export function compositeChildren(expected: Expected): CheckDraft[] {
  return records(expected, "children").map((child) => ({
    type: typeof child.type === "string" ? child.type : "",
    expected: isRecord(child.expected) ? child.expected : {},
  }));
}

export function isComplete(type: string, expected: Expected): boolean {
  switch (type) {
    case "agent_conversation_judge":
      return Boolean(text(expected, "rubric").trim());
    case "agent_response_contains":
    case "agent_response_not_contains":
    case "agent_text_equals":
      return Boolean(text(expected, "text").trim());
    case "agent_response_regex":
      return (
        Boolean(text(expected, "pattern")) &&
        compiles(text(expected, "pattern"))
      );
    case "agent_llm_judge":
      return Boolean(text(expected, "reference_text").trim());
    case "answer_correctness":
      return Boolean(text(expected, "gold_answer").trim());
    case "agent_ref_matches_kb_document":
      return strings(expected, "known_filenames").length > 0;
    case "agent_retrieved_documents":
      return records(expected, "documents").length > 0;
    case "agent_searched_knowledge_base":
      return strings(expected, "kb_ids").length > 0;
    case "agent_tool_called":
    case "agent_tool_not_called":
      return Boolean(text(expected, "tool_name"));
    case "agent_tool_arguments": {
      const args = expected.expected_args;
      return (
        Boolean(text(expected, "tool_name")) &&
        isRecord(args) &&
        Object.keys(args).length > 0
      );
    }
    case "agent_tool_arg_keys":
      return (
        Boolean(text(expected, "tool_name")) &&
        strings(expected, "present").length +
          strings(expected, "absent").length >
          0
      );
    case "agent_tool_argument_judge":
      return (
        Boolean(text(expected, "tool_name")) &&
        Boolean(text(expected, "argument_name")) &&
        Boolean(text(expected, "rubric").trim())
      );
    case "agent_tool_return_judge":
      return (
        Boolean(text(expected, "tool_name")) &&
        Boolean(text(expected, "rubric").trim())
      );
    case "agent_tool_return_contains":
      return (
        Boolean(text(expected, "tool_name")) &&
        Boolean(text(expected, "text").trim())
      );
    case "agent_any_of":
    case "agent_all_of": {
      const children = compositeChildren(expected);
      return (
        children.length > 0 &&
        children.every(
          (child) =>
            COMPOSITE_CHILD_TYPES.includes(child.type) &&
            isComplete(child.type, child.expected)
        )
      );
    }
    default:
      return true;
  }
}

export function argumentText(value: unknown): string {
  return typeof value === "string" ? value : JSON.stringify(value);
}

export function summarize(
  t: AgentTestsT,
  type: string,
  expected: Expected
): string {
  const tool = text(expected, "tool_name");
  switch (type) {
    case "agent_conversation_judge":
      return text(expected, "rubric");
    case "agent_response_contains":
      return t("checks.summary.contains", { text: text(expected, "text") });
    case "agent_response_not_contains":
      return t("checks.summary.notContains", { text: text(expected, "text") });
    case "agent_response_regex":
      return t("checks.summary.matches", {
        pattern: text(expected, "pattern"),
      });
    case "agent_text_equals":
      return t("checks.summary.equals", { text: text(expected, "text") });
    case "agent_llm_judge":
      return text(expected, "reference_text");
    case "answer_correctness":
      return text(expected, "gold_answer");
    case "answer_refusal":
      return expected.expect_refusal === false
        ? t("checks.summary.answers")
        : t("checks.summary.refuses");
    case "agent_has_ref_tags":
      return t("checks.summary.minCitations", {
        count: typeof expected.min_count === "number" ? expected.min_count : 1,
      });
    case "agent_ref_matches_kb_document":
      return t("checks.summary.citesOnly", {
        documents: strings(expected, "known_filenames").join(", "),
      });
    case "agent_retrieved_documents":
      return records(expected, "documents")
        .map((d) => text(d, "file_name"))
        .join(", ");
    case "agent_searched_knowledge_base": {
      const count = strings(expected, "kb_ids").length;
      return expected.no_search_ok === true
        ? t("checks.summary.knowledgeBasesAllowedOrNoSearch", { count })
        : t("checks.summary.knowledgeBasesAllowed", { count });
    }
    case "agent_tool_called":
      return t("checks.summary.toolCalled", { tool });
    case "agent_tool_not_called":
      return t("checks.summary.toolNotCalled", { tool });
    case "agent_tool_arguments": {
      const args = isRecord(expected.expected_args)
        ? expected.expected_args
        : {};
      const detail = Object.entries(args)
        .map(([name, value]) =>
          t("checks.summary.argumentValue", {
            name,
            value: argumentText(value),
          })
        )
        .join(", ");
      return t("checks.summary.toolDetail", { tool, detail });
    }
    case "agent_tool_arg_keys": {
      const detail = [
        ...strings(expected, "present").map((name) =>
          t("checks.summary.argumentPresent", { name })
        ),
        ...strings(expected, "absent").map((name) =>
          t("checks.summary.argumentAbsent", { name })
        ),
      ].join(", ");
      return t("checks.summary.toolDetail", { tool, detail });
    }
    case "agent_tool_argument_judge":
      return t("checks.summary.argumentJudge", {
        tool,
        argument: text(expected, "argument_name"),
        rubric: text(expected, "rubric"),
      });
    case "agent_tool_return_judge":
      return t("checks.summary.toolDetail", {
        tool,
        detail: text(expected, "rubric"),
      });
    case "agent_tool_return_contains":
      return t("checks.summary.toolReturns", {
        tool,
        text: text(expected, "text"),
      });
    case "agent_any_of":
    case "agent_all_of": {
      const children = compositeChildren(expected);
      const values = {
        count: children.length,
        checks: children.map((child) => checkLabel(t, child.type)).join(", "),
      };
      return type === "agent_any_of"
        ? t("checks.summary.anyOf", values)
        : t("checks.summary.allOf", values);
    }
    default:
      return "";
  }
}

// A summary names a check only once the field it is about is filled: before a
// tool, a knowledge base or a check is picked it would read ":", "0 knowledge
// bases" or "Any of 0:".
function namedBy(type: string, expected: Expected): boolean {
  if (type.startsWith("agent_tool")) return text(expected, "tool_name") !== "";
  if (type === "agent_searched_knowledge_base")
    return strings(expected, "kb_ids").length > 0;
  if (type === "agent_any_of" || type === "agent_all_of")
    return compositeChildren(expected).length > 0;
  return true;
}

// The name a check gets from its value until someone writes their own.
export function generatedName(
  t: AgentTestsT,
  type: string,
  expected: Expected
): string {
  const value = (key: string) => text(expected, key).trim();
  const named = (() => {
    switch (type) {
      case "agent_response_contains":
        return (
          value("text") && t("checks.names.mentions", { text: value("text") })
        );
      case "agent_response_not_contains":
        return (
          value("text") &&
          t("checks.names.notMentions", { text: value("text") })
        );
      case "agent_response_regex":
        return (
          value("pattern") &&
          t("checks.names.matches", { pattern: value("pattern") })
        );
      case "agent_text_equals":
        return (
          value("text") && t("checks.names.equals", { text: value("text") })
        );
      default:
        return namedBy(type, expected)
          ? summarize(t, type, expected).trim()
          : "";
    }
  })();
  const kind = kindOf(type);
  const name = named || (kind ? checkName(t, kind) : type);
  return name.length > MAX_ENTITY_NAME
    ? `${name.slice(0, MAX_ENTITY_NAME - 1).trimEnd()}…`
    : name;
}

// Checks saved before names were generated carry their kind's label.
export function checkTitle(t: AgentTestsT, check: CheckDraft): string {
  return check.name && check.name !== checkLabel(t, check.type)
    ? check.name
    : generatedName(t, check.type, check.expected);
}

export function compiles(pattern: string): boolean {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
}
